import React, { memo, useEffect, useState } from 'react';
import ErrorMessage from './ErrorMessage';
import { useTranslation } from 'react-i18next';

const DEFAULT_ERROR = '-1001';

/**
 * Errors that should be handled on UI side. Useful for complex error messages or for messages containing HTML tags.
 * Key is error code,
 * Value is function that returns translated text or JSX.
 */
const errorConfig = {
  '-1001': {
    key: 'ERROR-1001',
  },
};

/**
 * Utility component that creates ErrorMessages from API errors
 * @param error Axios error
 * @returns {JSX.Element} ErrorMessage
 */
const ApiErrorMessage = memo(({ error }) => {
  const { t } = useTranslation('error');

  const [showErrorMessage, setShowErrorMessage] = useState(false);

  /**
   * When error parameter changes, show popup.
   */
  useEffect(() => {
    if (!!error) {
      setShowErrorMessage(true);
    } else {
      setShowErrorMessage(false);
    }
  }, [error]);

  const closeErrorMessage = () => {
    setShowErrorMessage(false);
  };

  /**
   * Converts error to configuration for popup. It checks code and status of error.
   * If code is found between known errors, it will use UI error message.
   * If code isn't found and there is message from server, it will use server message.
   * If code isn't found and there is no server message, it uses default error message.
   * @param error error to transform
   * @returns {{tParams: null, message: null, key: null}} parameters for ErrorMesssage component.
   */
  const getErrorMessage = error => {
    let result = {
      key: null,
      tParams: null,
      message: null,
    };
    if (!!error) {
      let errorCode;
      if (!!error.response && !!error.response.data && !!error.response.data.code) {
        errorCode = error.response.data.code;
        result.message = error.response.data.status;
      } else {
        errorCode = DEFAULT_ERROR;
      }

      if (errorConfig.hasOwnProperty(errorCode)) {
        const config = errorConfig[errorCode];
        result.key = config.key;
        result.tParams = config.tParams;
      } else if (!result.message) {
        const config = errorConfig[DEFAULT_ERROR];
        result.key = config.key;
        result.tParams = config.tParams;
      }
    }
    return result;
  };

  let errorMessage = getErrorMessage(error);

  return (
    <ErrorMessage
      t={!!errorMessage.key && t}
      i18nKey={errorMessage.key}
      tParams={errorMessage.tParams}
      open={showErrorMessage}
      handleClose={closeErrorMessage}
      message={errorMessage.message}></ErrorMessage>
  );
});

export default ApiErrorMessage;